import * as THREE from 'three';
import { getCanvasResolution } from '../FikoCanvasResolution';

// Import all performance systems
import { FikoDynamicBatching, FikoStaticGeometryMerger } from './FikoDynamicBatching';
import { FikoDeferredSelection, FikoAsyncGPUSelection } from './FikoDeferredSelection';
import { FikoPredictiveRaycast, FikoSpatialRaycastAccelerator } from './FikoPredictiveRaycast';
import { FikoVertexQuantization, FikoGeometryCompression } from './FikoVertexQuantization';
import { FikoTemporalLOD, FikoProgressiveSceneLoader } from './FikoTemporalLOD';
import { FikoUnifiedComputePipeline, FikoAsyncComputeManager } from './FikoUnifiedComputePipeline';
import { FikoHierarchicalSelection } from './FikoHierarchicalSelection';
import { FikoAdaptiveQuality, FikoPerformanceProfiler } from './FikoAdaptiveQuality';
import { FikoRenderRaycastSync, FikoFrameCoordinator } from './FikoRenderRaycastSync';
import { FikoLargeGeometryHandler, FikoSmartRaycast } from './FikoLargeGeometryHandler';

// Post-Processing Effects (CAD görsel kalitesi için)
import { FikoScreenSpaceAO } from './FikoScreenSpaceAO';
import { FikoTemporalAA } from './FikoTemporalAA';
import { FikoZPrepass } from './FikoZPrepass';

/**
 * ULTRA PERFORMANS: Unified Performance Manager
 * Tüm optimizasyon sistemlerini tek noktadan yönetir
 *
 * Features:
 * - Centralized configuration
 * - Automatic system coordination
 * - Performance monitoring
 * - Debug visualization
 * - Hot-swappable systems
 */

interface PerformanceConfig {
  // Global
  enabled: boolean;
  targetFPS: number;
  debug: boolean;

  // Batching
  batchingEnabled: boolean;
  minMeshesForBatch: number;

  // Selection
  deferredSelectionEnabled: boolean;
  predictiveRaycastEnabled: boolean;
  hierarchicalSelectionEnabled: boolean;

  // Rendering
  temporalLODEnabled: boolean;
  adaptiveQualityEnabled: boolean;
  vertexQuantizationEnabled: boolean;

  // Compute
  unifiedComputeEnabled: boolean;

  // Render-Raycast Sync
  renderRaycastSyncEnabled: boolean;
  useDepthPicking: boolean;

  // Large Geometry
  largeGeometryHandlerEnabled: boolean;
  prewarmBVHOnImport: boolean;

  // Post-Processing Effects (CAD görsel kalitesi)
  ssaoEnabled: boolean;
  ssaoRadius: number;
  ssaoIntensity: number;
  temporalAAEnabled: boolean;
  taaSampleScale: number;
  zPrepassEnabled: boolean;
}

interface SystemStats {
  name: string;
  enabled: boolean;
  stats: Record<string, any>;
}

export class FikoPerformanceManager {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private domElement: HTMLElement;

  // Performance systems
  private dynamicBatching: FikoDynamicBatching | null = null;
  private staticMerger: FikoStaticGeometryMerger | null = null;
  private deferredSelection: FikoDeferredSelection | null = null;
  private asyncGPUSelection: FikoAsyncGPUSelection | null = null;
  private predictiveRaycast: FikoPredictiveRaycast | null = null;
  private spatialAccelerator: FikoSpatialRaycastAccelerator | null = null;
  private vertexQuantization: FikoVertexQuantization | null = null;
  private geometryCompression: FikoGeometryCompression | null = null;
  private temporalLOD: FikoTemporalLOD | null = null;
  private progressiveLoader: FikoProgressiveSceneLoader | null = null;
  private unifiedCompute: FikoUnifiedComputePipeline | null = null;
  private asyncCompute: FikoAsyncComputeManager | null = null;
  private hierarchicalSelection: FikoHierarchicalSelection | null = null;
  private adaptiveQuality: FikoAdaptiveQuality | null = null;
  private profiler: FikoPerformanceProfiler | null = null;
  private renderRaycastSync: FikoRenderRaycastSync | null = null;
  private frameCoordinator: FikoFrameCoordinator | null = null;
  private largeGeometryHandler: FikoLargeGeometryHandler | null = null;
  private smartRaycast: FikoSmartRaycast | null = null;

  // Post-Processing Systems (CAD görsel kalitesi)
  private screenSpaceAO: FikoScreenSpaceAO | null = null;
  private temporalAA: FikoTemporalAA | null = null;
  private zPrepass: FikoZPrepass | null = null;

  // Configuration
  private config: PerformanceConfig = {
    enabled: true,
    targetFPS: 60,
    debug: false,
    batchingEnabled: true,
    minMeshesForBatch: 2,
    deferredSelectionEnabled: true,
    predictiveRaycastEnabled: true,
    hierarchicalSelectionEnabled: true,
    temporalLODEnabled: true,
    adaptiveQualityEnabled: true,
    vertexQuantizationEnabled: true,
    unifiedComputeEnabled: true,
    renderRaycastSyncEnabled: true,
    useDepthPicking: true,
    largeGeometryHandlerEnabled: true,
    prewarmBVHOnImport: true,
    // Post-Processing - varsayılan olarak kapalı (isteğe bağlı açılır)
    ssaoEnabled: false,
    ssaoRadius: 16,
    ssaoIntensity: 1.0,
    temporalAAEnabled: false,
    taaSampleScale: 0.75,
    zPrepassEnabled: false
  };

  // Frame tracking
  private frameCount = 0;
  private lastUpdateTime = 0;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    domElement: HTMLElement
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.domElement = domElement;

    this.initializeSystems();
  }

  /**
   * Initialize all performance systems
   */
  private initializeSystems(): void {
    // Dynamic Batching
    if (this.config.batchingEnabled) {
      this.dynamicBatching = new FikoDynamicBatching(this.scene);
      this.staticMerger = new FikoStaticGeometryMerger();
    }

    // Selection Systems
    if (this.config.deferredSelectionEnabled) {
      this.deferredSelection = new FikoDeferredSelection(
        this.camera, this.scene, this.domElement
      );
    }

    if (this.config.predictiveRaycastEnabled) {
      this.predictiveRaycast = new FikoPredictiveRaycast(this.camera, this.scene);
      this.spatialAccelerator = new FikoSpatialRaycastAccelerator(this.scene, this.camera);
    }

    if (this.config.hierarchicalSelectionEnabled) {
      this.hierarchicalSelection = new FikoHierarchicalSelection(this.camera, this.scene);
    }

    // Rendering Systems
    if (this.config.temporalLODEnabled) {
      this.temporalLOD = new FikoTemporalLOD(this.camera, this.scene, this.renderer);
      this.progressiveLoader = new FikoProgressiveSceneLoader(this.scene, this.camera);
    }

    if (this.config.adaptiveQualityEnabled) {
      this.adaptiveQuality = new FikoAdaptiveQuality(this.renderer, this.scene, this.camera);
      this.profiler = new FikoPerformanceProfiler(this.renderer);
    }

    // Compression Systems
    if (this.config.vertexQuantizationEnabled) {
      this.vertexQuantization = new FikoVertexQuantization();
      this.geometryCompression = new FikoGeometryCompression();
    }

    // Compute Systems
    if (this.config.unifiedComputeEnabled) {
      this.unifiedCompute = new FikoUnifiedComputePipeline(this.renderer);
      this.asyncCompute = new FikoAsyncComputeManager(this.renderer);
    }

    // GPU Selection
    const size = this.renderer.getSize(new THREE.Vector2());
    this.asyncGPUSelection = new FikoAsyncGPUSelection(this.renderer, size.x, size.y);

    // Render-Raycast Sync (ULTRA PERFORMANS)
    if (this.config.renderRaycastSyncEnabled) {
      this.renderRaycastSync = new FikoRenderRaycastSync(
        this.camera, this.scene, this.renderer
      );
      this.renderRaycastSync.configure({
        useDepthPicking: this.config.useDepthPicking
      });
      this.frameCoordinator = new FikoFrameCoordinator(
        this.camera, this.scene, this.renderer
      );
    }

    // Large Geometry Handler (prevents UI freeze on first selection)
    if (this.config.largeGeometryHandlerEnabled) {
      this.largeGeometryHandler = new FikoLargeGeometryHandler();
      this.largeGeometryHandler.configure({
        prewarmOnImport: this.config.prewarmBVHOnImport
      });
      this.smartRaycast = new FikoSmartRaycast();
      // Auto-register scene
      this.smartRaycast.registerScene(this.scene);
    }

    // Post-Processing Systems (CAD görsel kalitesi için)
    // size değişkeni zaten yukarıda tanımlandı (satır 205)

    // Screen Space Ambient Occlusion - CAD modellerde derinlik algısı
    if (this.config.ssaoEnabled) {
      this.screenSpaceAO = new FikoScreenSpaceAO(this.renderer, size.x, size.y);
      this.screenSpaceAO.setRadius(this.config.ssaoRadius);
      this.screenSpaceAO.setIntensity(this.config.ssaoIntensity);
    }

    // Temporal Anti-Aliasing - Kenar yumuşatma (düşük çözünürlükte render, temporal filter)
    if (this.config.temporalAAEnabled && this.camera instanceof THREE.PerspectiveCamera) {
      this.temporalAA = new FikoTemporalAA(this.renderer, this.camera, size.x, size.y);
      this.temporalAA.setSampleScale(this.config.taaSampleScale);
    }

    // Z-Prepass - Overdraw eliminasyonu (%40-60 bandwidth azaltma)
    if (this.config.zPrepassEnabled) {
      this.zPrepass = new FikoZPrepass(this.renderer, size.x, size.y);
    }

    // All performance systems initialized
  }

  /**
   * Update all systems - call every frame
   */
  update(): void {
    if (!this.config.enabled) return;

    const now = performance.now();
    const deltaTime = now - this.lastUpdateTime;
    this.lastUpdateTime = now;
    this.frameCount++;

    // Update deferred selection
    if (this.deferredSelection) {
      this.deferredSelection.update();
    }

    // Update temporal LOD
    if (this.temporalLOD) {
      this.temporalLOD.resetFrameStats();
      this.temporalLOD.update();
    }

    // Update dynamic batching
    if (this.dynamicBatching) {
      this.dynamicBatching.update();
    }

    // Update adaptive quality
    if (this.adaptiveQuality) {
      this.adaptiveQuality.update();
    }

    // Record profiling
    if (this.profiler) {
      this.profiler.recordFrame(deltaTime);
    }

    // Progressive loading
    if (this.progressiveLoader) {
      this.progressiveLoader.processQueue();
    }

    // Render-Raycast Sync pre-render (shared culling)
    if (this.renderRaycastSync) {
      this.renderRaycastSync.preRender();
    }

    // Large geometry handler update (processes BVH build queue)
    if (this.largeGeometryHandler) {
      this.largeGeometryHandler.update();
    }
  }

  /**
   * Call after render to capture depth buffer
   */
  postRender(): void {
    if (this.renderRaycastSync) {
      this.renderRaycastSync.postRender();
    }
  }

  /**
   * Perform selection at screen position
   * Uses best available selection system with render-raycast sync
   * LARGE MESH SAFE: Uses progressive BVH to prevent UI freeze
   */
  select(screenX: number, screenY: number): THREE.Intersection | null {
    // Update predictive raycast
    if (this.predictiveRaycast) {
      this.predictiveRaycast.updateMousePosition(screenX, screenY);
    }

    // LARGE MESH SAFE: Use smart raycast for large geometries
    if (this.smartRaycast) {
      const result = this.smartRaycast.raycast(
        screenX, screenY, this.camera, this.scene
      );
      if (result.object) {
        // Convert to THREE.Intersection format
        return {
          object: result.object,
          point: result.point!,
          distance: result.distance,
          face: result.face,
          faceIndex: result.faceIndex ?? undefined,
          uv: undefined,
          uv1: undefined,
          normal: undefined,
          instanceId: undefined,
          pointOnLine: undefined,
          batchId: undefined
        };
      }
    }

    // FASTEST: Try render-raycast sync with depth picking first
    if (this.renderRaycastSync) {
      const raycaster = new THREE.Raycaster();
      raycaster.firstHitOnly = true;

      const _res = getCanvasResolution(this.renderer);
      const ndc = new THREE.Vector2(
        (screenX / _res.width) * 2 - 1,
        -(screenY / _res.height) * 2 + 1
      );
      raycaster.setFromCamera(ndc, this.camera);

      const result = this.renderRaycastSync.pick(screenX, screenY, raycaster);
      if (result) return result;
    }

    // Try hierarchical selection (very fast)
    if (this.hierarchicalSelection) {
      return this.hierarchicalSelection.select(screenX, screenY);
    }

    // Fall back to deferred selection
    if (this.deferredSelection) {
      const requestId = this.deferredSelection.requestSelection(
        screenX, screenY, 'high'
      );
      return this.deferredSelection.getLatestResult()?.point
        ? this.deferredSelection.getLatestResult() as any
        : null;
    }

    // Ultimate fallback
    return null;
  }

  /**
   * Request async selection (non-blocking)
   */
  requestAsyncSelection(
    screenX: number,
    screenY: number,
    callback: (result: THREE.Intersection | null) => void
  ): void {
    if (this.deferredSelection) {
      this.deferredSelection.requestSelection(
        screenX, screenY, 'high',
        (result) => {
          if (result && result.object) {
            callback({
              object: result.object,
              distance: result.distance,
              point: result.point!,
              face: null,
              faceIndex: result.faceIndex,
              uv: undefined,
              uv1: undefined,
              normal: result.normal || undefined,
              instanceId: result.instanceId,
              pointOnLine: undefined,
              batchId: undefined
            });
          } else {
            callback(null);
          }
        }
      );
    }
  }

  /**
   * Batch all scene meshes
   */
  batchScene(): void {
    if (this.dynamicBatching) {
      this.dynamicBatching.batchScene();
    }
  }

  /**
   * Compress all scene geometry
   */
  compressScene(): void {
    if (this.geometryCompression) {
      const result = this.geometryCompression.compressScene(this.scene);
      // Compressed meshes: result.meshesCompressed, saved: result.totalSaved bytes
    }
  }

  /**
   * Register object for temporal LOD
   */
  registerForTemporalLOD(object: THREE.Object3D, importance: number = 0.5): void {
    if (this.temporalLOD) {
      this.temporalLOD.registerObject(object, importance);
    }
  }

  /**
   * Invalidate spatial caches (call after scene changes)
   */
  invalidateCaches(): void {
    if (this.hierarchicalSelection) {
      this.hierarchicalSelection.invalidateGrid();
    }

    if (this.spatialAccelerator) {
      const size = this.renderer.getSize(new THREE.Vector2());
      this.spatialAccelerator.rebuild(size.x, size.y);
    }

    if (this.dynamicBatching) {
      // Dynamic batching handles this automatically
    }
  }

  /**
   * Get all system statistics
   */
  getAllStats(): SystemStats[] {
    const stats: SystemStats[] = [];

    if (this.dynamicBatching) {
      stats.push({
        name: 'Dynamic Batching',
        enabled: this.config.batchingEnabled,
        stats: this.dynamicBatching.getStats()
      });
    }

    if (this.deferredSelection) {
      stats.push({
        name: 'Deferred Selection',
        enabled: this.config.deferredSelectionEnabled,
        stats: this.deferredSelection.getStats()
      });
    }

    if (this.predictiveRaycast) {
      stats.push({
        name: 'Predictive Raycast',
        enabled: this.config.predictiveRaycastEnabled,
        stats: this.predictiveRaycast.getStats()
      });
    }

    if (this.hierarchicalSelection) {
      stats.push({
        name: 'Hierarchical Selection',
        enabled: this.config.hierarchicalSelectionEnabled,
        stats: this.hierarchicalSelection.getStats()
      });
    }

    if (this.temporalLOD) {
      stats.push({
        name: 'Temporal LOD',
        enabled: this.config.temporalLODEnabled,
        stats: this.temporalLOD.getStats()
      });
    }

    if (this.adaptiveQuality) {
      stats.push({
        name: 'Adaptive Quality',
        enabled: this.config.adaptiveQualityEnabled,
        stats: this.adaptiveQuality.getStats()
      });
    }

    if (this.vertexQuantization) {
      stats.push({
        name: 'Vertex Quantization',
        enabled: this.config.vertexQuantizationEnabled,
        stats: this.vertexQuantization.getStats()
      });
    }

    if (this.unifiedCompute) {
      stats.push({
        name: 'Unified Compute',
        enabled: this.config.unifiedComputeEnabled,
        stats: this.unifiedCompute.getStats()
      });
    }

    if (this.renderRaycastSync) {
      stats.push({
        name: 'Render-Raycast Sync',
        enabled: this.config.renderRaycastSyncEnabled,
        stats: this.renderRaycastSync.getStats()
      });
    }

    if (this.frameCoordinator) {
      stats.push({
        name: 'Frame Coordinator',
        enabled: this.config.renderRaycastSyncEnabled,
        stats: this.frameCoordinator.getTimings()
      });
    }

    if (this.largeGeometryHandler) {
      stats.push({
        name: 'Large Geometry Handler',
        enabled: this.config.largeGeometryHandlerEnabled,
        stats: this.largeGeometryHandler.getStats()
      });
    }

    if (this.smartRaycast) {
      stats.push({
        name: 'Smart Raycast',
        enabled: this.config.largeGeometryHandlerEnabled,
        stats: this.smartRaycast.getStats()
      });
    }

    // Post-Processing Stats
    if (this.screenSpaceAO) {
      stats.push({
        name: 'Screen Space AO',
        enabled: this.config.ssaoEnabled,
        stats: this.screenSpaceAO.getStats()
      });
    }

    if (this.temporalAA) {
      stats.push({
        name: 'Temporal AA',
        enabled: this.config.temporalAAEnabled,
        stats: this.temporalAA.getStats()
      });
    }

    if (this.zPrepass) {
      stats.push({
        name: 'Z-Prepass',
        enabled: this.config.zPrepassEnabled,
        stats: this.zPrepass.getStats()
      });
    }

    return stats;
  }

  /**
   * Print all stats to console
   */
  logStats(): void {
    // Stats logging disabled - use getAllStats() for programmatic access
  }

  /**
   * Configure system
   */
  configure(config: Partial<PerformanceConfig>): void {
    Object.assign(this.config, config);

    // Reinitialize affected systems
    // (In production, would be more granular)
  }

  /**
   * Get current configuration
   */
  getConfig(): PerformanceConfig {
    return { ...this.config };
  }

  /**
   * Set quality preset
   */
  setQualityPreset(preset: 'ultra' | 'high' | 'medium' | 'low' | 'potato'): void {
    if (this.adaptiveQuality) {
      this.adaptiveQuality.setQuality(preset);
    }
  }

  /**
   * Start performance recording
   */
  startRecording(): void {
    if (this.profiler) {
      this.profiler.startRecording();
    }
  }

  /**
   * Stop recording and get report
   */
  stopRecording(): ReturnType<FikoPerformanceProfiler['getReport']> | null {
    if (this.profiler) {
      this.profiler.stopRecording();
      return this.profiler.getReport();
    }
    return null;
  }

  /**
   * Handle resize
   */
  resize(width: number, height: number): void {
    if (this.asyncGPUSelection) {
      this.asyncGPUSelection.resize(width, height);
    }

    if (this.adaptiveQuality) {
      this.adaptiveQuality.resize(width, height);
    }

    if (this.temporalLOD) {
      this.temporalLOD.resize(width, height);
    }

    if (this.spatialAccelerator) {
      this.spatialAccelerator.rebuild(width, height);
    }

    if (this.renderRaycastSync) {
      this.renderRaycastSync.resize(width, height);
    }

    if (this.frameCoordinator) {
      this.frameCoordinator.resize(width, height);
    }
  }

  /**
   * Cleanup all systems
   */
  dispose(): void {
    if (this.dynamicBatching) this.dynamicBatching.dispose();
    if (this.staticMerger) this.staticMerger.dispose();
    if (this.deferredSelection) this.deferredSelection.dispose();
    if (this.asyncGPUSelection) this.asyncGPUSelection.dispose();
    if (this.predictiveRaycast) this.predictiveRaycast.dispose();
    if (this.spatialAccelerator) this.spatialAccelerator.dispose();
    if (this.vertexQuantization) this.vertexQuantization.dispose();
    if (this.geometryCompression) this.geometryCompression.dispose();
    if (this.temporalLOD) this.temporalLOD.dispose();
    if (this.unifiedCompute) this.unifiedCompute.dispose();
    if (this.asyncCompute) this.asyncCompute.dispose();
    if (this.hierarchicalSelection) this.hierarchicalSelection.dispose();
    if (this.adaptiveQuality) this.adaptiveQuality.dispose();
    if (this.renderRaycastSync) this.renderRaycastSync.dispose();
    if (this.frameCoordinator) this.frameCoordinator.dispose();
    if (this.largeGeometryHandler) this.largeGeometryHandler.dispose();
    if (this.smartRaycast) this.smartRaycast.dispose();

    // Post-Processing cleanup
    if (this.screenSpaceAO) this.screenSpaceAO.dispose();
    if (this.temporalAA) this.temporalAA.dispose();
    if (this.zPrepass) this.zPrepass.dispose();

    // All performance systems disposed
  }

  // ============================================================
  // POST-PROCESSING PUBLIC API
  // ============================================================

  /**
   * Enable/disable Screen Space Ambient Occlusion
   * CAD modellerde derinlik algısını artırır
   */
  setSSAOEnabled(enabled: boolean): void {
    this.config.ssaoEnabled = enabled;
    if (enabled && !this.screenSpaceAO) {
      const size = this.renderer.getSize(new THREE.Vector2());
      this.screenSpaceAO = new FikoScreenSpaceAO(this.renderer, size.x, size.y);
      this.screenSpaceAO.setRadius(this.config.ssaoRadius);
      this.screenSpaceAO.setIntensity(this.config.ssaoIntensity);
    }
    if (this.screenSpaceAO) {
      this.screenSpaceAO.setEnabled(enabled);
    }
  }

  /**
   * Configure SSAO parameters
   */
  configureSSAO(radius: number, intensity: number): void {
    this.config.ssaoRadius = radius;
    this.config.ssaoIntensity = intensity;
    if (this.screenSpaceAO) {
      this.screenSpaceAO.setRadius(radius);
      this.screenSpaceAO.setIntensity(intensity);
    }
  }

  /**
   * Enable/disable Temporal Anti-Aliasing
   * Düşük çözünürlükte render edip temporal filter ile kaliteli görüntü
   */
  setTemporalAAEnabled(enabled: boolean): void {
    this.config.temporalAAEnabled = enabled;
    if (enabled && !this.temporalAA && this.camera instanceof THREE.PerspectiveCamera) {
      const size = this.renderer.getSize(new THREE.Vector2());
      this.temporalAA = new FikoTemporalAA(this.renderer, this.camera, size.x, size.y);
      this.temporalAA.setSampleScale(this.config.taaSampleScale);
    }
    if (this.temporalAA) {
      this.temporalAA.setEnabled(enabled);
    }
  }

  /**
   * Enable/disable Z-Prepass
   * Overdraw eliminasyonu ile %40-60 bandwidth azaltma
   */
  setZPrepassEnabled(enabled: boolean): void {
    this.config.zPrepassEnabled = enabled;
    if (enabled && !this.zPrepass) {
      const size = this.renderer.getSize(new THREE.Vector2());
      this.zPrepass = new FikoZPrepass(this.renderer, size.x, size.y);
    }
    if (this.zPrepass) {
      this.zPrepass.setEnabled(enabled);
    }
  }

  /**
   * Run Z-Prepass before main render
   * Returns depth texture for use in other passes
   */
  runZPrepass(): THREE.DepthTexture | null {
    if (this.zPrepass && this.config.zPrepassEnabled) {
      return this.zPrepass.renderZPrepass(this.scene, this.camera);
    }
    return null;
  }

  /**
   * Get SSAO system for advanced usage
   */
  getSSAO(): FikoScreenSpaceAO | null {
    return this.screenSpaceAO;
  }

  /**
   * Get TAA system for advanced usage
   */
  getTemporalAA(): FikoTemporalAA | null {
    return this.temporalAA;
  }

  /**
   * Get Z-Prepass system for advanced usage
   */
  getZPrepass(): FikoZPrepass | null {
    return this.zPrepass;
  }

  /**
   * Execute coordinated frame (render + raycast in one call)
   * ULTRA PERFORMANS: Shared culling, depth picking, minimal overhead
   */
  executeCoordinatedFrame(
    mouseX: number,
    mouseY: number,
    renderCallback: (visibleObjects: any[]) => void
  ): THREE.Intersection | null {
    if (this.frameCoordinator) {
      return this.frameCoordinator.executeFrame(mouseX, mouseY, renderCallback);
    }

    // Fallback: manual update + select
    this.update();
    renderCallback([]);
    this.postRender();
    return this.select(mouseX, mouseY);
  }

  /**
   * Check if object is currently visible (from last frame)
   */
  isObjectVisible(obj: THREE.Object3D): boolean {
    if (this.renderRaycastSync) {
      return this.renderRaycastSync.isObjectVisible(obj);
    }
    return obj.visible;
  }

  /**
   * Get render-raycast sync system for advanced usage
   */
  getRenderRaycastSync(): FikoRenderRaycastSync | null {
    return this.renderRaycastSync;
  }

  /**
   * Get frame coordinator for advanced usage
   */
  getFrameCoordinator(): FikoFrameCoordinator | null {
    return this.frameCoordinator;
  }

  /**
   * Get large geometry handler for advanced usage
   */
  getLargeGeometryHandler(): FikoLargeGeometryHandler | null {
    return this.largeGeometryHandler;
  }

  /**
   * Get smart raycast for advanced usage
   */
  getSmartRaycast(): FikoSmartRaycast | null {
    return this.smartRaycast;
  }

  /**
   * Register new mesh for large geometry handling
   * Call this when importing a new mesh
   */
  registerMesh(mesh: THREE.Mesh): void {
    if (this.largeGeometryHandler) {
      this.largeGeometryHandler.registerMesh(mesh);
    }
    if (this.smartRaycast) {
      this.smartRaycast.getLargeHandler().registerMesh(mesh);
    }
  }

  /**
   * Pre-warm BVH for all registered large meshes
   * Call this after scene is fully loaded
   */
  prewarmBVH(): void {
    if (this.largeGeometryHandler) {
      this.largeGeometryHandler.prewarmAll();
    }
    if (this.smartRaycast) {
      this.smartRaycast.prewarm();
    }
  }

  /**
   * Check if BVH is ready for a mesh
   */
  isBVHReady(mesh: THREE.Mesh): boolean {
    if (this.largeGeometryHandler) {
      return this.largeGeometryHandler.isBVHReady(mesh);
    }
    return true;
  }

  /**
   * Get BVH build progress for a mesh (0-100)
   */
  getBVHProgress(mesh: THREE.Mesh): number {
    if (this.largeGeometryHandler) {
      return this.largeGeometryHandler.getBVHProgress(mesh);
    }
    return 100;
  }
}

// Export all systems for direct access
export {
  FikoDynamicBatching,
  FikoStaticGeometryMerger,
  FikoDeferredSelection,
  FikoAsyncGPUSelection,
  FikoPredictiveRaycast,
  FikoSpatialRaycastAccelerator,
  FikoVertexQuantization,
  FikoGeometryCompression,
  FikoTemporalLOD,
  FikoProgressiveSceneLoader,
  FikoUnifiedComputePipeline,
  FikoAsyncComputeManager,
  FikoHierarchicalSelection,
  FikoAdaptiveQuality,
  FikoPerformanceProfiler,
  FikoRenderRaycastSync,
  FikoFrameCoordinator,
  FikoLargeGeometryHandler,
  FikoSmartRaycast,
  // Post-Processing Systems
  FikoScreenSpaceAO,
  FikoTemporalAA,
  FikoZPrepass
};
